import React from "react"
import { AUTH_LAYOUT, LIST, PROFILE, CANCEL } from "constants/index"
import { NAVIGATE_TO_PROFILE_PAGE } from "constants/events"
import { LAYOUT_TYPE } from "types/general.types"
import HomeIcon from "components/atoms/_icons/HomeIcon"
import SearchIcon from "components/atoms/_icons/SearchIcon"
import ProfileIcon from "components/atoms/_icons/ProfileIcon"
import NotificationBellIcon from "components/atoms/_icons/NotificationBellIcon"
import CancelIcon from "components/atoms/_icons/CancelIcon"
import FeedPage from "pages/feed"
import LoginPage from "pages/login"
import RegisterPage from "pages/register"
import ProfilePage from "pages/user/[username]"
import WishListPage from "pages/user/[username]/list/[listId]"
import EditListPage from "pages/user/[username]/list/[listId]/edit"
import WishItemPage from "pages/user/[username]/item/[itemId]"
import EditItemPage from "pages/user/[username]/item/[itemId]/edit"
import WishItemsPage from "pages/user/[username]/items"
import CloseFriendsPage from "pages/user/[username]/closeFriends"
import LandingPage from "pages/landing"
import SettingsPage from "pages/settings"
import ExplorePage from "pages/explore"
import CreateNewListPage from "pages/list"
import CreateNewItemPage from "pages/item"
import NotificationsPage from "pages/notifications"
import NewConnectionsPage from "pages/notifications/new-connections"
import EditUserProfilePage from "pages/account/edit"
import PremiumPlansPage from "pages/premium/plans"
import CheckoutPage from "pages/checkout"
import PaymentResultPage from "pages/payment/result"

const PAGES = {
  login: {
    path: "/login",
    title: "auth.login",
    key: "login",
    useLayoutWithPadding: true,
    component: <LoginPage/>,
    layoutType: AUTH_LAYOUT as LAYOUT_TYPE
  },
  register: {
    path: "/register",
    title: "auth.register",
    key: "register",
    useLayoutWithPadding: true,
    component: <RegisterPage/>,
    layoutType: AUTH_LAYOUT as LAYOUT_TYPE
  },
  landing: {
    path: "/",
    title: "",
    key: "landing",
    isProtected: false,
    component: <LandingPage/>
  },
  home: {
    path: "/feed",
    title: "",
    key: "home",
    icon: <HomeIcon/>,
    selectedIcon: <HomeIcon variant="contained"/>,
    showInNavigation: true,
    orderInNavigation: 1,
    isProtected: true,
    component: <FeedPage/>
  },
  explore: {
    path: "/explore",
    title: "",
    key: "explore",
    icon: <SearchIcon/>,
    selectedIcon: <SearchIcon variant="contained"/>,
    showInNavigation: true,
    orderInNavigation: 2,
    useLayoutWithPadding: true,
    isProtected: true,
    component: <ExplorePage/>
  },
  notifications: {
    path: "/notifications",
    title: "",
    key: "notifications",
    icon: <NotificationBellIcon/>,
    selectedIcon: <NotificationBellIcon variant="contained"/>,
    showInNavigation: true,
    orderInNavigation: 3,
    useLayoutWithPadding: true,
    isProtected: true,
    component: <NotificationsPage/>
  },
  newConnections: {
    path: "/notifications/new-connections",
    title: "connections.newConnections",
    key: "newConnections",
    hasBackButton: true,
    useLayoutWithPadding: true,
    isProtected: true,
    component: <NewConnectionsPage/>
  },
  profile: {
    path: "/user/:handle",
    title: "",
    key: PROFILE,
    icon: <ProfileIcon/>,
    selectedIcon: <ProfileIcon variant="contained"/>,
    hasBackButton: true,
    showInNavigation: true,
    orderInNavigation: 4,
    isProtected: true,
    component: <ProfilePage/>
  },
  editUserProfile: {
    path: "/account/edit",
    title: "user.updateProfile",
    key: PROFILE,
    hasBackButton: true,
    useLayoutWithPadding: true,
    isProtected: true,
    component: <EditUserProfilePage/>
  },
  closeFriends: {
    path: "/user/:handle/close-friends",
    title: "collections.closeFriends",
    key: PROFILE,
    icon: <ProfileIcon/>,
    selectedIcon: <ProfileIcon variant="contained"/>,
    hasBackButton: true,
    showInNavigation: false,
    useLayoutWithPadding: true,
    isProtected: true,
    component: <CloseFriendsPage/>
  },
  list: {
    path: "/list",
    title: "collections.addNewList",
    key: "newList",
    hasBackButton: true,
    useLayoutWithPadding: true,
    isProtected: true,
    component: <CreateNewListPage/>
  },
  viewList: {
    path: "/user/:handle/list/:listId",
    title: "",
    key: LIST,
    hasBackButton: true,
    useLayoutWithPadding: true,
    isProtected: true,
    component: <WishListPage/>
  },
  editList: {
    path: "/user/:handle/list/:listId/edit",
    title: "collections.editList",
    key: "editList",
    hasBackButton: true,
    useLayoutWithPadding: true,
    isProtected: true,
    component: <EditListPage/>
  },
  item: {
    path: "/item",
    title: "items.addNewItem",
    key: "newItem",
    hasBackButton: true,
    useLayoutWithPadding: true,
    isProtected: true,
    component: <CreateNewItemPage/>
  },
  viewItem: {
    path: "/user/:handle/item/:itemId",
    title: "",
    key: "viewItem",
    hasBackButton: true,
    isProtected: true,
    component: <WishItemPage/>
  },
  viewItems: {
    path: "/user/:handle/items",
    title: "",
    key: "viewItems",
    hasBackButton: true,
    isProtected: true,
    component: <WishItemsPage/>
  },
  editItem: {
    path: "/user/:handle/item/:itemId/edit",
    title: "items.editItem",
    key: "editItem",
    hasBackButton: true,
    useLayoutWithPadding: true,
    isProtected: true,
    component: <EditItemPage/>
  },
  settings: {
    path: "/settings",
    title: "settings.title",
    key: "settings",
    hasBackButton: true,
    isProtected: true,
    component: <SettingsPage/>
  },
  premiumSubscription: {
    path: "/premium/plans",
    title: "premium.purchaseSubscription",
    key: "subscription",
    hasBackButton: true,
    isProtected: true,
    useLayoutWithPadding: true,
    component: <PremiumPlansPage/>
  },
  checkout: {
    path: "/checkout/:productId",
    title: "checkout.title",
    key: "checkout",
    hasBackButton: true,
    isProtected: true,
    useLayoutWithPadding: true,
    component: <CheckoutPage/>
  },
  paymentResult: {
    path: "/payment/result",
    title: "checkout.title",
    key: "paymentResult",
    hasBackButton: false,
    headerButtons: {
      right: null,
      left: [{ icon: <CancelIcon id="no-stroke"/>, eventName: NAVIGATE_TO_PROFILE_PAGE, key: CANCEL }]
    },
    isProtected: true,
    useLayoutWithPadding: true,
    component: <PaymentResultPage/>
  }
}

export const ITEM_VIEW_PAGES = [
  PAGES.viewList.path,
  PAGES.viewItem.path,
  PAGES.viewItems.path
]

export const PUBLIC_PAGES = [PAGES.profile, PAGES.viewItem, PAGES.viewList, PAGES.viewItems]

export default PAGES
